import { motion } from 'framer-motion';
import { Shield, Eye, Database, Mail, Phone, User, Lock } from 'lucide-react';
import styled from 'styled-components';

const PrivacyContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding-top: 80px;
`;

const Section = styled.section`
  padding: 4rem 0;
  
  @media (max-width: 768px) {
    padding: 2rem 0;
  }
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  
  h1 {
    font-size: clamp(2rem, 5vw, 3rem);
    font-weight: 800;
    color: #1a202c;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.125rem;
    color: #64748b;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;

const PrivacyContent = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 3rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  
  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1a202c;
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1a202c;
    margin-bottom: 1rem;
    margin-top: 2rem;
  }
  
  p {
    color: #64748b;
    line-height: 1.8;
    margin-bottom: 1rem;
  }
  
  ul {
    margin-bottom: 1rem;
    
    li {
      color: #64748b;
      line-height: 1.6;
      margin-bottom: 0.5rem;
      margin-left: 1.5rem;
    }
  }
  
  .highlight {
    color: #3b82f6;
    font-weight: 600;
  }
  
  .rights-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
    margin: 2rem 0;
    
    .right-card {
      background: #f8fafc;
      border-radius: 0.5rem;
      padding: 1.5rem;
      
      .right-icon {
        color: #3b82f6;
        margin-bottom: 1rem;
      }
      
      h4 {
        font-weight: 600;
        color: #1a202c;
        margin-bottom: 0.5rem;
      }
      
      p {
        font-size: 0.875rem;
        color: #64748b;
        margin-bottom: 0;
      }
    }
  }
`;

const PrivacyPage = () => {
  return (
    <PrivacyContainer>
      <Section>
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <PageHeader>
              <h1>Politique de Confidentialité</h1>
              <p>Comment nous protégeons et utilisons vos données personnelles</p>
            </PageHeader>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <PrivacyContent>
              <h2>
                <Shield size={24} />
                Notre engagement
              </h2>
              <p>
                Chez <span className="highlight">INZA STORE</span>, nous nous engageons à protéger votre vie privée et à sécuriser
                vos données personnelles conformément à la loi n°2013-450 du 19 juin 2013 relative à la protection des données personnelles en Côte d'Ivoire.
              </p>

              <h3>Données collectées</h3>
              <p>Nous collectons les informations suivantes pour vous fournir nos services :</p>
              <ul>
                <li><span className="highlight">Informations d'identité</span> : Nom, prénom, adresse email, numéro de téléphone</li>
                <li><span className="highlight">Informations de livraison</span> : Adresse postale, ville, code postal</li>
                <li><span className="highlight">Informations de paiement</span> : Moyens de paiement (cryptés), historique d'achats</li>
                <li><span className="highlight">Données techniques</span> : Adresse IP, type de navigateur, cookies</li>
                <li><span className="highlight">Préférences</span> : Centre d'intérêt, historique de navigation</li>
              </ul>

              <h2>
                <Database size={24} />
                Utilisation des données
              </h2>
              
              <h3>Vos données sont utilisées pour :</h3>
              <ul>
                <li>Traiter et exécuter vos commandes</li>
                <li>Vous fournir un service client personnalisé</li>
                <li>Améliorer nos produits et services</li>
                <li>Vous envoyer des offres commerciales (avec votre consentement)</li>
                <li>Assurer la sécurité et la prévention des fraudes</li>
                <li>Respecter nos obligations légales</li>
              </ul>

              <h3>Base légale du traitement</h3>
              <p>Nous traitons vos données sur les bases légales suivantes :</p>
              <ul>
                <li><span className="highlight">Consentement</span> : Pour les newsletters et marketing</li>
                <li><span className="highlight">Exécution contractuelle</span> : Pour traiter vos commandes</li>
                <li><span className="highlight">Obligation légale</span> : Pour la facturation et la comptabilité</li>
                <li><span className="highlight">Intérêt légitime</span> : Pour la sécurité et l'amélioration des services</li>
              </ul>

              <h2>
                <Eye size={24} />
                Vos droits
              </h2>
              <p>Conformément à la législation ivoirienne, vous disposez des droits suivants :</p>
              
              <div className="rights-grid">
                <div className="right-card">
                  <User className="right-icon" size={24} />
                  <h4>Droit d'accès</h4>
                  <p>Accéder à toutes vos données personnelles</p>
                </div>
                <div className="right-card">
                  <Lock className="right-icon" size={24} />
                  <h4>Droit de rectification</h4>
                  <p>Corriger vos données inexactes</p>
                </div>
                <div className="right-card">
                  <Database className="right-icon" size={24} />
                  <h4>Droit de suppression</h4>
                  <p>Demander la suppression de vos données</p>
                </div>
                <div className="right-card">
                  <Shield className="right-icon" size={24} />
                  <h4>Droit d'opposition</h4>
                  <p>Vous opposer au traitement de vos données</p>
                </div>
              </div>

              <h2>
                <Lock size={24} />
                Sécurité des données
              </h2>
              
              <h3>Mesures de sécurité</h3>
              <p>Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données :</p>
              <ul>
                <li>Chiffrement SSL/TLS pour toutes les communications</li>
                <li>Chiffrement des données de paiement</li>
                <li>Contrôle d'accès strict à nos systèmes</li>
                <li>Sauvegardes régulières et sécurisées</li>
                <li>Formation continue de notre équipe</li>
              </ul>

              <h3>Conservation des données</h3>
              <p>Vos données sont conservées pendant la durée nécessaire aux finalités pour lesquelles elles ont été collectées :</p>
              <ul>
                <li><span className="highlight">Données clients</span> : 10 ans après la dernière relation commerciale</li>
                <li><span className="highlight">Données comptables</span> : 10 ans (obligation légale)</li>
                <li><span className="highlight">Cookies</span> : 13 mois maximum</li>
                <li><span className="highlight">Marketing</span> : Jusqu'à désabonnement</li>
              </ul>

              <h2>
                <Mail size={24} />
                Partage des données
              </h2>
              
              <h3>Nous ne partageons vos données qu'avec :</h3>
              <ul>
                <li><span className="highlight">Prestataires de services</span> : Livraison, paiement, hébergement</li>
                <li><span className="highlight">Partenaires commerciaux</span> : Avec votre consentement explicite</li>
                <li><span className="highlight">Autorités légales</span> : En cas d'obligation légale</li>
              </ul>

              <h3>Transferts internationaux</h3>
              <p>
                Vos données sont hébergées en Côte d'Ivoire et dans l'Union Européenne.
                Tous les transferts respectent les standards de protection adéquats.
              </p>

              <h2>
                <Shield size={24} />
                Cookies et traceurs
              </h2>
              
              <h3>Types de cookies utilisés</h3>
              <ul>
                <li><span className="highlight">Cookies essentiels</span> : Nécessaires au fonctionnement du site</li>
                <li><span className="highlight">Cookies de performance</span> : Pour analyser l'utilisation du site</li>
                <li><span className="highlight">Cookies de personnalisation</span> : Pour mémoriser vos préférences</li>
                <li><span className="highlight">Cookies marketing</span> : Avec votre consentement</li>
              </ul>

              <h3>Gestion des cookies</h3>
              <p>Vous pouvez gérer vos préférences cookies via :</p>
              <ul>
                <li>Notre bandeau de cookies</li>
                <li>Les paramètres de votre navigateur</li>
                <li>Votre espace client</li>
              </ul>

              <h3>Contact DPO</h3>
              <p>
                Pour toute question concernant vos données personnelles, contactez notre
                Délégué à la Protection des Données (DPO) :
              </p>
              <ul>
                <li>Email : dpo@inza-store.ci</li>
                <li>Téléphone : +225 20 00 00 00</li>
                <li>Adresse : Abidjan, Plateau, Face à la BCEAO</li>
              </ul>

              <h3>Mise à jour</h3>
              <p>
                Cette politique de confidentialité peut être mise à jour pour refléter les changements
                dans nos pratiques ou pour des raisons légales. La date de la dernière mise à jour
                est indiquée en bas de cette page.
              </p>

              <p style={{ marginTop: '2rem', fontSize: '0.875rem', color: '#9ca3af' }}>
                Dernière mise à jour : Décembre 2024
              </p>
            </PrivacyContent>
          </motion.div>
        </Container>
      </Section>
    </PrivacyContainer>
  );
};

export default PrivacyPage;
